import { AxiosError } from 'axios';
import { WafBlockedError } from './api-errors';

/**
 * Coarse classification used to decide whether (and how) an error is worth reporting to Sentry:
 * - 'expected': normal, anticipated outcome - never reported. Only ever set by an explicit signal
 *   (the backend's own classification), never guessed.
 * - 'external': cause lies outside our code (third-party/remote system, network/offline, WAF,
 *   permission) - reported, but leveled separately from bugs.
 * - 'bug': known programming error - always reported.
 * - 'unknown': fail-open default - always reported.
 */
export type ErrorClassification = 'expected' | 'external' | 'bug' | 'unknown';

// Status codes treated as 'external' when nothing more specific classified the error. Narrow on purpose -
// 400/409/422 can also mean the frontend sent something it shouldn't have, so those stay 'unknown'.
const EXTERNAL_STATUS_CODES = new Set([401, 403, 404, 429, 503]);

// Values the backend may assert on an error response body's `classification` field - see
// probonio-api's ServiceExceptionClassification. Excludes 'unknown', a frontend-only fallback.
const BACKEND_ASSERTABLE_CLASSIFICATIONS = new Set<ErrorClassification>(['expected', 'external', 'bug']);

// Generalizes the `cause`-field pattern WafBlockedError uses: trust the backend's own classification
// over guessing from the status code.
function readBackendClassification(error: AxiosError): ErrorClassification | undefined {
  const value = (error.response?.data as { classification?: unknown } | undefined)?.classification;
  return typeof value === 'string' && BACKEND_ASSERTABLE_CLASSIFICATIONS.has(value as ErrorClassification)
    ? (value as ErrorClassification)
    : undefined;
}

export function classifyError(error: unknown): ErrorClassification {
  if (error instanceof WafBlockedError) {
    return 'external';
  }
  if (error instanceof AxiosError) {
    if (!error.response) {
      // no response at all - offline, timeout, DNS/CORS failure, etc.
      return 'external';
    }
    const backendClassification = readBackendClassification(error);
    if (backendClassification) {
      return backendClassification;
    }
    if (EXTERNAL_STATUS_CODES.has(error.response.status)) {
      return 'external';
    }
  }
  return 'unknown';
}

export function shouldReport(classification: ErrorClassification): boolean {
  return classification !== 'expected';
}
